import {
  ConfirmButton,
  type ConfirmButtonProps,
} from "@/v2-backoffice-client/components/button"
import { useUserCustomerMismatchPlanRunner } from "@/v2-backoffice-client/user/customerMismatch/userCustomerMismatch.resolve"
import {
  buildMismatchPlan,
  describeMismatchPlanMoves,
  describeMismatchPlanSkips,
  describeMismatchPlanTargets,
  type MismatchPlanStrategy,
} from "@/v2-backoffice-client/user/customerMismatch/userCustomerMismatch.utils"
import type { UserCustomerMismatchFragment } from "@/v2-backoffice-client/user/customerMismatch/__types__/user.customerMismatch.fragment"

export type UserCustomerMismatchResolveManyButtonProps = Omit<
  ConfirmButtonProps,
  "onConfirm" | "children"
> & {
  mismatches: UserCustomerMismatchFragment[]
  strategy: MismatchPlanStrategy
  onCompleted?(): void
}

const STRATEGY_COPY: Record<
  MismatchPlanStrategy,
  { label: string; iconSrc: ConfirmButtonProps["iconSrc"]; content: string }
> = {
  suggested: {
    label: "Resolve as Suggested",
    iconSrc: "check",
    content:
      "Take each selected row's highlighted choice: the move its team's other members point at, or both sides to the customer HubSpot merged them into. Mixed teams are left for the row's own buttons.",
  },
  "users-to-teams": {
    label: "Users → Teams' Customers",
    iconSrc: "user",
    content:
      "Move every selected user to the customer their team belongs to. Their licenses and seats follow.",
  },
  "teams-to-users": {
    label: "Teams → Users' Customers",
    iconSrc: "users",
    content:
      "Relink every selected team, with all its members, to the customer that owns the selected user.",
  },
}

/**
 * The bulk form of the row's choices. "Resolve as Suggested" takes each
 * row's highlighted button; the two directional buttons force one
 * direction on every selected row. A row whose two sides HubSpot merged
 * into one customer has no direction to choose and moves whole under all
 * three. The confirmation spells out what moves, where, and what is left
 * out and why.
 */
function UserCustomerMismatchResolveManyButton(
  props: UserCustomerMismatchResolveManyButtonProps,
) {
  const { mismatches, strategy, onCompleted, ...confirmButtonProps } = props
  const copy = STRATEGY_COPY[strategy]

  const { run, isRunning } = useUserCustomerMismatchPlanRunner()
  const plan = buildMismatchPlan(mismatches, strategy)
  const hasMoves = plan.teamMoves.length > 0 || plan.userMoves.length > 0

  const summary = hasMoves
    ? `Move ${describeMismatchPlanMoves(plan)} to ${describeMismatchPlanTargets(plan)}, putting ${plan.resolvedRows} of ${mismatches.length} selected row${mismatches.length === 1 ? "" : "s"} right?`
    : "Nothing in the selection can be moved this way."
  const mergedNote =
    plan.mergedRows > 0
      ? ` ${plan.mergedRows} row${plan.mergedRows === 1 ? "" : "s"} move${plan.mergedRows === 1 ? "s" : ""} whole: HubSpot merged both sides' companies into one customer.`
      : ""

  return (
    <ConfirmButton
      isStacked
      variant={strategy === "suggested" ? "warning" : "ghost"}
      iconSrc={copy.iconSrc}
      content={copy.content}
      confirmContent={`${summary}${mergedNote}${describeMismatchPlanSkips(plan)}`}
      confirmVariant="warning"
      isLoading={isRunning}
      disabled={!hasMoves}
      onConfirm={async () => {
        if (await run(plan)) onCompleted?.()
      }}
      {...confirmButtonProps}
    >
      {copy.label}
    </ConfirmButton>
  )
}

export default UserCustomerMismatchResolveManyButton
